import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';

// Configure axios base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Helper function to set user language from preferences
    const setUserLanguage = (userData) => {
        if (userData?.preferences?.preferredLanguage) {
            i18n.changeLanguage(userData.preferences.preferredLanguage);
        }
        // If no preference, i18n will use browser language (already configured in i18n.js)
    };

    // Logout helper for use in interceptor
    const handleSessionExpired = () => {
        console.log('[Auth] Session expired, clearing authentication and redirecting');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        // Redirect to login with session expired flag
        window.location.href = '/login?sessionExpired=true';
    };

    useEffect(() => {
        const initAuth = async () => {
            // Check if user is logged in on mount
            const token = localStorage.getItem('token');
            const userData = localStorage.getItem('user');

            if (token && userData) {
                const parsedUser = JSON.parse(userData);
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                // If authProvider or subscriptionExpiry is missing, fetch fresh user data from server
                if (!parsedUser.authProvider || !parsedUser.subscriptionExpiry) {
                    try {
                        const response = await axios.get('/api/auth/me');
                        const freshUserData = response.data;
                        localStorage.setItem('user', JSON.stringify(freshUserData));
                        setUser(freshUserData);
                        setUserLanguage(freshUserData);
                    } catch (error) {
                        console.error('Failed to fetch user data:', error);
                        // Fall back to existing data if fetch fails
                        setUser(parsedUser);
                        setUserLanguage(parsedUser);
                    }
                } else {
                    setUser(parsedUser);
                    setUserLanguage(parsedUser);
                }
            }

            setLoading(false);
        };

        initAuth();

        // Setup axios response interceptor to handle session expiration
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                // Check if error is 401 Unauthorized (token expired/invalid)
                if (error.response?.status === 401) {
                    const url = error.config?.url || 'unknown';
                    const message = error.response?.data?.message || '';

                    console.log('[Auth Interceptor] 401 error detected', {
                        url,
                        message,
                        method: error.config?.method,
                    });

                    // Don't trigger session expiration for login endpoint
                    // Users may have invalid credentials
                    if (url.includes('/api/auth/login')) {
                        console.log('[Auth Interceptor] Ignoring 401 from login endpoint');
                        return Promise.reject(error);
                    }

                    // Check if the error message indicates token failure
                    if (message.includes('token') || message.includes('authorized')) {
                        console.log('[Auth Interceptor] Token failure detected, logging out');
                        handleSessionExpired();
                    } else {
                        console.log('[Auth Interceptor] 401 but not token-related, passing through');
                    }
                }
                return Promise.reject(error);
            }
        );

        // Cleanup interceptor on unmount
        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    const login = async (email, password) => {
        console.log('[Auth] Login attempt started', { email });
        try {
            const response = await axios.post('/api/auth/login', { email, password });
            console.log('[Auth] Login response received', {
                status: response.status,
                hasToken: !!response.data.token
            });
            const { token, ...userData } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userData));
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            setUser(userData);
            setUserLanguage(userData);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed',
            };
        }
    };

    const register = async (userData) => {
        try {
            const response = await axios.post('/api/auth/register', userData);
            const { token, ...user } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            setUser(user);
            setUserLanguage(user);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed',
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
    };

    const setAuthData = ({ token, user: userData }) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setUserLanguage(userData);
    };

    const hasFeatureAccess = (feature) => {
        if (!user) return false;
        return user.accessibleFeatures?.includes(feature) || false;
    };

    const isSubscriptionActive = () => {
        if (!user) return false;
        // FREE-TRIAL without an expiry date means the data is stale/missing — treat as expired
        if (!user.subscriptionExpiry) return user.subscription !== 'FREE-TRIAL';
        return new Date(user.subscriptionExpiry) > new Date();
    };

    const refreshUser = async () => {
        try {
            const response = await axios.get('/api/auth/me');
            const userData = response.data;
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setUserLanguage(userData);
        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    };

    const isAdmin = () => {
        if (!user) return false;
        return user.role === 'admin';
    };

    const isProUser = () => {
        if (!user) return false;
        return user.role === 'admin' || user.role === 'proUser';
    };

    const updateUserPreferences = async (preferences) => {
        try {
            const response = await axios.put('/api/auth/preferences', preferences);
            const updatedUser = response.data;

            // Update localStorage and state
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            // Update language if preference was changed
            if (preferences.preferredLanguage) {
                setUserLanguage(updatedUser);
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update preferences',
            };
        }
    };

    const value = {
        user,
        setUser: (userData) => {
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
        },
        loading,
        login,
        register,
        logout,
        setAuthData,
        hasFeatureAccess,
        isSubscriptionActive,
        isAdmin,
        isProUser,
        updateUserPreferences,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
